
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

const AGENT_TYPES = [
  { id: 'sales', label: 'Agent Vente', icon: 'ri-shopping-bag-line', color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/30', conversionBoost: 0.40, supportRatio: 0 },
  { id: 'support', label: 'Agent Support', icon: 'ri-customer-service-2-line', color: 'text-cyan-400', bg: 'bg-cyan-400/10 border-cyan-400/30', conversionBoost: 0.15, supportRatio: 0.70 },
  { id: 'marketing', label: 'Agent Marketing', icon: 'ri-megaphone-line', color: 'text-violet-400', bg: 'bg-violet-400/10 border-violet-400/30', conversionBoost: 0.30, supportRatio: 0.20 },
  { id: 'hr', label: 'Agent RH', icon: 'ri-team-line', color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/30', conversionBoost: 0.10, supportRatio: 0.50 },
];

const SLIDER_CONFIG = [
  {
    id: 'leads',
    label: 'Leads mensuels actuels',
    icon: 'ri-user-add-line',
    min: 50,
    max: 5000,
    step: 50,
    unit: 'leads',
    defaultValue: 300,
    color: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'conversionRate',
    label: 'Taux de conversion actuel',
    icon: 'ri-percent-line',
    min: 1,
    max: 30,
    step: 0.5,
    unit: '%',
    defaultValue: 5,
    color: 'from-cyan-500 to-sky-500',
  },
  {
    id: 'avgRevenue',
    label: 'Revenu moyen par client',
    icon: 'ri-money-euro-circle-line',
    min: 100,
    max: 10000,
    step: 100,
    unit: '€',
    defaultValue: 500,
    color: 'from-violet-500 to-purple-500',
  },
  {
    id: 'supportAgents',
    label: 'Agents support actuels',
    icon: 'ri-group-line',
    min: 0,
    max: 20,
    step: 1,
    unit: 'pers.',
    defaultValue: 2,
    color: 'from-orange-500 to-amber-500',
  },
];

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toFixed(0);
}

function formatEuro(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k€`;
  return `${n.toFixed(0)}€`;
}

interface SliderValues {
  leads: number;
  conversionRate: number;
  avgRevenue: number;
  supportAgents: number;
}

export default function ROICalculator() {
  const [selectedAgent, setSelectedAgent] = useState(AGENT_TYPES[0]);
  const [values, setValues] = useState<SliderValues>({
    leads: 300,
    conversionRate: 5,
    avgRevenue: 500,
    supportAgents: 2,
  });
  const [showDetails, setShowDetails] = useState(false);

  const results = useMemo(() => {
    const { leads, conversionRate, avgRevenue, supportAgents } = values;
    const agent = selectedAgent;

    // Revenus actuels
    const currentClients = leads * (conversionRate / 100);
    const currentRevenue = currentClients * avgRevenue;

    // Avec Agent IA
    const newConversionRate = conversionRate * (1 + agent.conversionBoost);
    const newClients = leads * (newConversionRate / 100);
    const newRevenue = newClients * avgRevenue;
    const revenueGain = newRevenue - currentRevenue;

    // Économies support
    const avgSalaryCost = 2800; // coût mensuel moyen chargé
    const automationRatio = agent.supportRatio;
    const supportSavings = supportAgents * avgSalaryCost * automationRatio;

    // Coût abonnement (plan Pro)
    const subscriptionCost = 199;

    // ROI total
    const totalGain = revenueGain + supportSavings;
    const netGain = totalGain - subscriptionCost;
    const roi = subscriptionCost > 0 ? ((netGain / subscriptionCost) * 100) : 0;

    // Payback en jours
    const paybackDays = totalGain > 0 ? Math.round((subscriptionCost / totalGain) * 30) : 0;

    return {
      currentRevenue,
      newRevenue,
      revenueGain,
      supportSavings,
      totalGain,
      netGain,
      roi,
      paybackDays,
      newConversionRate,
      newClients,
      currentClients,
    };
  }, [values, selectedAgent]);

  const handleSlider = (id: keyof SliderValues, val: number) => {
    setValues(prev => ({ ...prev, [id]: val }));
  };

  const roiColor =
    results.roi >= 500 ? 'text-emerald-400' :
    results.roi >= 200 ? 'text-cyan-400' :
    'text-yellow-400';

  return (
    <section id="roi-calculator" className="py-32 bg-gradient-to-b from-black to-cyber-dark relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyber-blue/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400/10 border border-emerald-400/30 mb-6">
            <i className="ri-calculator-line text-emerald-400 text-sm" />
            <span className="text-emerald-400 text-sm font-body font-semibold">Calculateur de ROI</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-4">
            Calculez votre gain potentiel
          </h2>
          <p className="text-gray-400 font-body text-lg max-w-2xl mx-auto">
            Entrez vos données actuelles et découvrez en temps réel combien un agent IA peut vous rapporter chaque mois.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">

          {/* ── LEFT: Inputs ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Agent selector */}
            <div>
              <p className="text-white font-body font-semibold mb-4 flex items-center gap-2">
                <i className="ri-robot-line text-cyber-blue" />
                Quel agent souhaitez-vous déployer ?
              </p>
              <div className="grid grid-cols-2 gap-3">
                {AGENT_TYPES.map(agent => (
                  <button
                    key={agent.id}
                    onClick={() => setSelectedAgent(agent)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer ${
                      selectedAgent.id === agent.id
                        ? `${agent.bg} border-opacity-100`
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${agent.icon} text-xl ${selectedAgent.id === agent.id ? agent.color : 'text-gray-400'}`} />
                    </div>
                    <span className={`text-sm font-body font-medium ${selectedAgent.id === agent.id ? 'text-white' : 'text-gray-400'}`}>
                      {agent.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div className="space-y-6">
              {SLIDER_CONFIG.map(cfg => (
                <div key={cfg.id}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-300 font-body text-sm flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cfg.icon} text-base text-gray-400`} />
                      </div>
                      {cfg.label}
                    </label>
                    <span className="text-white font-body font-bold text-sm bg-white/10 px-3 py-1 rounded-full">
                      {cfg.unit === '€'
                        ? `${values[cfg.id as keyof SliderValues].toLocaleString('fr-FR')}€`
                        : cfg.unit === '%'
                        ? `${values[cfg.id as keyof SliderValues]}%`
                        : `${values[cfg.id as keyof SliderValues]} ${cfg.unit}`}
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="range"
                      min={cfg.min}
                      max={cfg.max}
                      step={cfg.step}
                      value={values[cfg.id as keyof SliderValues]}
                      onChange={e => handleSlider(cfg.id as keyof SliderValues, parseFloat(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${
                          ((values[cfg.id as keyof SliderValues] - cfg.min) / (cfg.max - cfg.min)) * 100
                        }%, rgba(255,255,255,0.1) ${
                          ((values[cfg.id as keyof SliderValues] - cfg.min) / (cfg.max - cfg.min)) * 100
                        }%, rgba(255,255,255,0.1) 100%)`
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 mt-1 font-body">
                    <span>{cfg.unit === '€' ? `${cfg.min}€` : cfg.unit === '%' ? `${cfg.min}%` : `${cfg.min} ${cfg.unit}`}</span>
                    <span>{cfg.unit === '€' ? `${cfg.max.toLocaleString('fr-FR')}€` : cfg.unit === '%' ? `${cfg.max}%` : `${cfg.max} ${cfg.unit}`}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── RIGHT: Results ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="sticky top-24"
          >
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl p-8 backdrop-blur-xl">

              {/* ROI Principal */}
              <div className="text-center mb-8 pb-8 border-b border-white/10">
                <p className="text-gray-400 font-body text-sm mb-2">Retour sur investissement mensuel</p>
                <motion.div
                  key={results.roi.toFixed(0)}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className={`text-7xl font-display font-bold ${roiColor} mb-1`}
                >
                  {formatNumber(results.roi)}%
                </motion.div>
                <p className="text-gray-500 font-body text-xs">
                  Remboursé en <strong className="text-white">{results.paybackDays} jours</strong>
                </p>
              </div>

              {/* Métriques clés */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  {
                    label: 'Gain revenus',
                    value: formatEuro(results.revenueGain),
                    icon: 'ri-line-chart-line',
                    color: 'text-emerald-400',
                    bg: 'bg-emerald-400/10',
                  },
                  {
                    label: 'Économies RH',
                    value: formatEuro(results.supportSavings),
                    icon: 'ri-team-line',
                    color: 'text-cyan-400',
                    bg: 'bg-cyan-400/10',
                  },
                  {
                    label: 'Gain total/mois',
                    value: formatEuro(results.totalGain),
                    icon: 'ri-money-euro-circle-line',
                    color: 'text-violet-400',
                    bg: 'bg-violet-400/10',
                  },
                  {
                    label: 'Gain net/mois',
                    value: formatEuro(results.netGain),
                    icon: 'ri-trophy-line',
                    color: 'text-yellow-400',
                    bg: 'bg-yellow-400/10',
                  },
                ].map((m, i) => (
                  <motion.div
                    key={m.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`${m.bg} rounded-2xl p-4`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center mb-2">
                      <i className={`${m.icon} ${m.color} text-lg`} />
                    </div>
                    <div className={`text-xl font-display font-bold ${m.color}`}>{m.value}</div>
                    <div className="text-gray-400 text-xs font-body mt-0.5">{m.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Détails toggle */}
              <button
                onClick={() => setShowDetails(v => !v)}
                className="w-full flex items-center justify-between text-sm text-gray-400 hover:text-white transition-colors font-body mb-4 cursor-pointer"
              >
                <span>Voir le détail du calcul</span>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-${showDetails ? 'up' : 'down'}-s-line text-lg`} />
                </div>
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mb-6"
                  >
                    <div className="bg-white/5 rounded-2xl p-4 space-y-3 text-sm font-body">
                      <div className="flex justify-between text-gray-400">
                        <span>Clients actuels/mois</span>
                        <span className="text-white">{results.currentClients.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Clients avec IA/mois</span>
                        <span className="text-emerald-400">+{(results.newClients - results.currentClients).toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Taux conversion actuel</span>
                        <span className="text-white">{values.conversionRate}%</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Taux conversion avec IA</span>
                        <span className="text-emerald-400">{results.newConversionRate.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Revenu actuel/mois</span>
                        <span className="text-white">{formatEuro(results.currentRevenue)}</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Revenu avec IA/mois</span>
                        <span className="text-emerald-400">{formatEuro(results.newRevenue)}</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex justify-between text-gray-400">
                        <span>Coût abonnement</span>
                        <span className="text-red-400">-199€/mois</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* CTA */}
              <Link
                to="/demo"
                className="block w-full py-4 text-center bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-body font-semibold rounded-full hover:shadow-2xl hover:shadow-cyber-blue/40 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Obtenir ce ROI — Essai gratuit
              </Link>
              <p className="text-center text-gray-600 text-xs font-body mt-3">
                Sans CB · Sans engagement · Résultats en 7 jours
              </p>
            </div>
          </motion.div>

        </div>

        {/* Bottom disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-gray-600 text-xs font-body mt-12"
        >
          * Estimations basées sur les moyennes observées chez nos 500+ clients PME. Les résultats réels peuvent varier selon votre secteur et votre configuration.
        </motion.p>
      </div>

      <style>{`
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #06b6d4;
          cursor: pointer;
          border: 3px solid #0e1117;
          box-shadow: 0 0 8px rgba(6,182,212,0.6);
        }
        input[type='range']::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #06b6d4;
          cursor: pointer;
          border: 3px solid #0e1117;
          box-shadow: 0 0 8px rgba(6,182,212,0.6);
        }
      `}</style>
    </section>
  );
}
